import { useEffect, useState } from 'react';
import { StatCards } from '@/components/stat-cards';
import { FilterBar } from '@/components/filter-bar';
import { SkuGrid } from '@/components/sku-grid';
import { Pagination } from '@/components/pagination';
import { useFilterState } from '@/hooks/use-filter-state';
import { useMonitorList } from '@/hooks/use-monitor-list';
import { getStats, getBrands } from '@/lib/api';
import type { Stats, BrandStat } from '@/lib/api';
import { PAGE_LIMIT } from '@/lib/constants';

export interface ProductMapRouteProps {
  brands: BrandStat[];
  onBrandsLoaded: (b: BrandStat[]) => void;
}

export default function ProductMapRoute({ brands, onBrandsLoaded }: ProductMapRouteProps) {
  const { filter, page, onFilterChange, onReset, onPageChange } = useFilterState();
  const { data, meta, loading, error } = useMonitorList(filter, page);

  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    getStats()
      .then((s) => !cancelled && setStats(s))
      .catch((err) => console.error('Stats load failed:', err))
      .finally(() => !cancelled && setStatsLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (brands.length > 0) return;
    let cancelled = false;
    getBrands()
      .then((b) => !cancelled && onBrandsLoaded(b))
      .catch((err) => console.error('Brands load failed:', err));
    return () => {
      cancelled = true;
    };
  }, [brands.length, onBrandsLoaded]);

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Product Map</h1>
        <p className="text-sm text-muted-foreground">Quản lý SKUs màn hình GearVN</p>
      </header>

      <StatCards stats={stats} loading={statsLoading} />

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <FilterBar state={filter} brands={brands} onChange={onFilterChange} onReset={onReset} />
      </div>

      <SkuGrid
        monitors={data}
        loading={loading}
        error={error}
        onClearFilters={onReset}
        onSelect={(m) => console.log('Click SKU:', m.id)}
      />

      <Pagination meta={meta} pageSize={PAGE_LIMIT} onPageChange={onPageChange} />
    </div>
  );
}
